import {
  ACCESS_GROUP_SYNC_ACTION,
  ACCESS_GROUP_SYNC_BATCH_LIMIT
} from '../config/accessGroupSync';
import { normalizeMobileNumber } from '../config/keyRequest';
import {
  mobileEqualsFilter,
  odataFilterParam
} from '../lib/odataQuery';
import { formatDataverseRecordKey } from '../lib/dataverseRecordId';
import {
  geoJsonPolygonToRing,
  parseMapPolygon,
  pointInPolygon,
  serializeMapPolygon
} from '../lib/geo';
import { createAccessGroupSyncService } from './accessGroupSyncService';
import { getAccessGroup, listAccessGroups } from './unlocKeyService';

const GROUP_SELECT =
  'oc_tilgangsgruppeid,oc_name,oc_beskrivelse,oc_unlocid,oc_defaultforlocks,oc_defaultfortelefoner,oc_lastsyncedon,oc_mappolygon,statecode,statuscode';
const GROUP_SELECT_BASIC =
  'oc_tilgangsgruppeid,oc_name,oc_beskrivelse,oc_unlocid,oc_defaultforlocks,oc_defaultfortelefoner,oc_lastsyncedon,statecode,statuscode';
const LOCK_NAV = 'oc_Tilgangsgruppe_Lock';
const PHONE_NAV = 'oc_Tilgangsgruppe_Telefon';

function mapGroup(record, { locks = [], phones = [] } = {}) {
  return {
    id: record.oc_tilgangsgruppeid,
    groupId: record.oc_tilgangsgruppeid,
    name: record.oc_name,
    description: record.oc_beskrivelse || '',
    unlocId: record.oc_unlocid || null,
    defaultForLocks: Boolean(record.oc_defaultforlocks),
    defaultForPhones: Boolean(record.oc_defaultfortelefoner),
    lastSyncedOn: record.oc_lastsyncedon || null,
    mapPolygon: parseMapPolygon(record.oc_mappolygon),
    stateCode: record.statecode,
    statusCode: record.statuscode,
    locks,
    phones
  };
}

function mapLockRef(record) {
  return {
    id: record.oc_lockid,
    lockId: record.oc_lockid,
    name: record.oc_name,
    externalId: record.oc_externalid || record.oc_lockid
  };
}

function mapPhoneRef(record) {
  return {
    id: record.oc_telefonid,
    phoneId: record.oc_telefonid,
    name: record.oc_name,
    mobileNumber: record.oc_mobilnummer
  };
}

function chunkPayload(payload) {
  const {
    lockIdsToAdd = [],
    lockIdsToRemove = [],
    appUserIdsToAdd = [],
    appUserIdsToRemove = [],
    inviteNotification
  } = payload;

  const addChanges = lockIdsToAdd.length * (appUserIdsToAdd.length || 1) +
    lockIdsToAdd.length * appUserIdsToRemove.length +
    lockIdsToRemove.length * appUserIdsToAdd.length;
  const removeChanges = lockIdsToRemove.length * (appUserIdsToRemove.length || 1);

  if (addChanges + removeChanges <= ACCESS_GROUP_SYNC_BATCH_LIMIT) {
    return [payload];
  }

  const batches = [];
  const lockAdds = [...lockIdsToAdd];
  const lockRemoves = [...lockIdsToRemove];
  const userAdds = [...appUserIdsToAdd];
  const userRemoves = [...appUserIdsToRemove];

  while (lockAdds.length || lockRemoves.length || userAdds.length || userRemoves.length) {
    const batch = {
      lockIdsToAdd: [],
      lockIdsToRemove: [],
      appUserIdsToAdd: [],
      appUserIdsToRemove: []
    };
    if (inviteNotification) {
      batch.inviteNotification = inviteNotification;
    }
    let used = 0;

    const take = (source, target, weight = 1) => {
      while (source.length && used + weight <= ACCESS_GROUP_SYNC_BATCH_LIMIT) {
        target.push(source.shift());
        used += weight;
      }
    };

    take(lockAdds, batch.lockIdsToAdd, Math.max(1, userAdds.length || userRemoves.length || 1));
    take(lockRemoves, batch.lockIdsToRemove, Math.max(1, userAdds.length || userRemoves.length || 1));
    take(userAdds, batch.appUserIdsToAdd, Math.max(1, lockAdds.length || lockRemoves.length || 1));
    take(userRemoves, batch.appUserIdsToRemove, Math.max(1, lockAdds.length || lockRemoves.length || 1));

    if (!batch.lockIdsToAdd.length && !batch.lockIdsToRemove.length &&
        !batch.appUserIdsToAdd.length && !batch.appUserIdsToRemove.length) {
      break;
    }

    batches.push(batch);
  }

  return batches.length ? batches : [payload];
}

const SILENT_INVITE = { mode: 'none' };

function extractUnlocGroup(unlocResponse) {
  return unlocResponse?.accessGroup || unlocResponse || {};
}

function extractUnlocLockIds(accessGroup) {
  if (Array.isArray(accessGroup.lockIds)) {
    return accessGroup.lockIds.filter(Boolean);
  }

  if (accessGroup.locks && typeof accessGroup.locks === 'object') {
    return Object.keys(accessGroup.locks);
  }

  return [];
}

function extractUnlocAppUserIds(accessGroup) {
  if (Array.isArray(accessGroup.appUserIds)) {
    return accessGroup.appUserIds.filter(Boolean);
  }

  if (accessGroup.appUsers && typeof accessGroup.appUsers === 'object') {
    return Object.keys(accessGroup.appUsers);
  }

  return [];
}

async function resolveLockIdsByExternalIds(dataverseApiCall, externalIds) {
  const unique = [...new Set(externalIds.filter(Boolean))];
  const lockIds = [];

  for (let i = 0; i < unique.length; i += 15) {
    const batch = unique.slice(i, i + 15);
    const filter = batch
      .map((id) => `oc_externalid eq '${String(id).replace(/'/g, "''")}'`)
      .join(' or ');
    const response = await dataverseApiCall(
      `/oc_locks?$select=oc_lockid,oc_externalid&$filter=${filter}`
    );
    for (const row of response.value || []) {
      lockIds.push(row.oc_lockid);
    }
  }

  return lockIds;
}

async function resolvePhoneIdsByMobileNumbers(dataverseApiCall, mobileNumbers) {
  const unique = [
    ...new Set(
      mobileNumbers
        .map((mobile) => normalizeMobileNumber(mobile))
        .filter(Boolean)
    )
  ];
  const phoneIds = [];

  for (let i = 0; i < unique.length; i += 15) {
    const batch = unique.slice(i, i + 15);
    const filter = odataFilterParam(
      batch.map((mobile) => mobileEqualsFilter('oc_mobilnummer', mobile)).join(' or ')
    );
    const response = await dataverseApiCall(
      `/oc_telefons?$select=oc_telefonid,oc_mobilnummer&$filter=${filter}`
    );
    for (const row of response.value || []) {
      phoneIds.push(row.oc_telefonid);
    }
  }

  return phoneIds;
}

export function createAccessGroupService(dataverseApiCall, getEnvironmentUrl) {
  const syncService = createAccessGroupSyncService(dataverseApiCall);

  const listGroupsPaged = async (filter) => {
    const groups = [];
    let path =
      `/oc_tilgangsgruppes?$select=${GROUP_SELECT}&$filter=${filter}&$orderby=oc_name asc`;

    while (path) {
      const response = await dataverseApiCall(path);
      groups.push(...(response.value || []).map((record) => mapGroup(record)));

      const nextLink = response['@odata.nextLink'];
      if (!nextLink) {
        break;
      }

      const apiPrefix = `${getEnvironmentUrl()}/api/data/v9.2`;
      path = nextLink.startsWith(apiPrefix) ? nextLink.slice(apiPrefix.length) : nextLink;
    }

    return groups;
  };

  const listDefaultGroups = async (fieldName) =>
    listGroupsPaged(`${fieldName} eq true and statecode eq 0`);

  const listPolygonGroups = async () =>
    listGroupsPaged('oc_mappolygon ne null and statecode eq 0');

  const isAlreadyAssociatedError = (error) => {
    const message = String(error?.message || error || '');
    return /already|exist|duplicate|Cannot insert|associated/i.test(message);
  };

  const toFiniteNumber = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (value === undefined || value === null || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const associateLock = async (groupId, lockId) => {
    const environmentUrl = getEnvironmentUrl();
    await dataverseApiCall(
      `/oc_tilgangsgruppes(${formatDataverseRecordKey(groupId)})/${LOCK_NAV}/$ref`,
      {
        method: 'POST',
        body: JSON.stringify({
          '@odata.id': `${environmentUrl}/api/data/v9.2/oc_locks(${formatDataverseRecordKey(lockId)})`
        })
      }
    );
  };

  const disassociateLock = async (groupId, lockId) => {
    await dataverseApiCall(
      `/oc_tilgangsgruppes(${formatDataverseRecordKey(groupId)})/${LOCK_NAV}(${formatDataverseRecordKey(lockId)})/$ref`,
      { method: 'DELETE' }
    );
  };

  const associatePhone = async (groupId, phoneId) => {
    const environmentUrl = getEnvironmentUrl();
    await dataverseApiCall(
      `/oc_tilgangsgruppes(${formatDataverseRecordKey(groupId)})/${PHONE_NAV}/$ref`,
      {
        method: 'POST',
        body: JSON.stringify({
          '@odata.id': `${environmentUrl}/api/data/v9.2/oc_telefons(${formatDataverseRecordKey(phoneId)})`
        })
      }
    );
  };

  const disassociatePhone = async (groupId, phoneId) => {
    await dataverseApiCall(
      `/oc_tilgangsgruppes(${formatDataverseRecordKey(groupId)})/${PHONE_NAV}(${formatDataverseRecordKey(phoneId)})/$ref`,
      { method: 'DELETE' }
    );
  };

  const loadMembers = async (groupId) => {
    const key = formatDataverseRecordKey(groupId);
    const expand =
      `&$expand=${LOCK_NAV}($select=oc_lockid,oc_name,oc_externalid),` +
      `${PHONE_NAV}($select=oc_telefonid,oc_name,oc_mobilnummer)`;

    try {
      const response = await dataverseApiCall(
        `/oc_tilgangsgruppes(${key})?$select=${GROUP_SELECT}${expand}`
      );
      const locks = (response[LOCK_NAV] || []).map(mapLockRef);
      const phones = (response[PHONE_NAV] || []).map(mapPhoneRef);
      return mapGroup(response, { locks, phones });
    } catch (error) {
      console.warn('Access group getById with oc_mappolygon failed, retrying without:', error);
      const response = await dataverseApiCall(
        `/oc_tilgangsgruppes(${key})?$select=${GROUP_SELECT_BASIC}${expand}`
      );
      const locks = (response[LOCK_NAV] || []).map(mapLockRef);
      const phones = (response[PHONE_NAV] || []).map(mapPhoneRef);
      return mapGroup(response, { locks, phones });
    }
  };

  const applyMembershipLocal = async (groupId, { lockIds = [], phoneIds = [] }) => {
    const current = await loadMembers(groupId);
    const currentLockIds = new Set(current.locks.map((lock) => lock.id));
    const currentPhoneIds = new Set(current.phones.map((phone) => phone.id));
    const nextLockIds = new Set(lockIds);
    const nextPhoneIds = new Set(phoneIds);

    for (const lockId of [...nextLockIds].filter((id) => !currentLockIds.has(id))) {
      await associateLock(groupId, lockId);
    }
    for (const lockId of [...currentLockIds].filter((id) => !nextLockIds.has(id))) {
      await disassociateLock(groupId, lockId);
    }
    for (const phoneId of [...nextPhoneIds].filter((id) => !currentPhoneIds.has(id))) {
      await associatePhone(groupId, phoneId);
    }
    for (const phoneId of [...currentPhoneIds].filter((id) => !nextPhoneIds.has(id))) {
      await disassociatePhone(groupId, phoneId);
    }

    return loadMembers(groupId);
  };

  const touchLastSynced = async (groupId) => {
    await dataverseApiCall(`/oc_tilgangsgruppes(${formatDataverseRecordKey(groupId)})`, {
      method: 'PATCH',
      body: JSON.stringify({ oc_lastsyncedon: new Date().toISOString() })
    });
  };

  const enqueueMembershipSync = async ({ groupId, groupName, payload, wait = false }) => {
    const batches = chunkPayload(payload);
    let lastSync = null;

    for (const batch of batches) {
      lastSync = await syncService.create({
        action: ACCESS_GROUP_SYNC_ACTION.UPDATE_MEMBERSHIP,
        groupId,
        groupName,
        payload: batch
      });

      if (wait) {
        await syncService.waitForCompletion(lastSync.syncId);
      }
    }

    return lastSync;
  };

  const getAllGroups = async ({ includeInactive = false } = {}) => {
    const filter = includeInactive ? '' : '&$filter=statecode eq 0';
    try {
      const response = await dataverseApiCall(
        `/oc_tilgangsgruppes?$select=${GROUP_SELECT}&$orderby=oc_name asc${filter}`
      );
      return (response.value || []).map((row) => mapGroup(row));
    } catch (error) {
      console.warn('Access group query with oc_mappolygon failed, retrying without:', error);
      const response = await dataverseApiCall(
        `/oc_tilgangsgruppes?$select=${GROUP_SELECT_BASIC}&$orderby=oc_name asc${filter}`
      );
      return (response.value || []).map((row) => mapGroup(row));
    }
  };

  const syncMembershipFromUnloc = async (groupId, unlocGroupOverride = null) => {
    const current = await loadMembers(groupId);
    if (!current.unlocId) {
      throw new Error('Gruppen er ikke koblet til Unloc. Importer fra Unloc eller vent til opprettelse er ferdig.');
    }

    const accessGroup = unlocGroupOverride || extractUnlocGroup(await getAccessGroup(current.unlocId));
    const lockIds = await resolveLockIdsByExternalIds(
      dataverseApiCall,
      extractUnlocLockIds(accessGroup)
    );
    const phoneIds = await resolvePhoneIdsByMobileNumbers(
      dataverseApiCall,
      extractUnlocAppUserIds(accessGroup)
    );

    await applyMembershipLocal(groupId, { lockIds, phoneIds });

    const metaPayload = {};
    if (accessGroup.name && accessGroup.name !== current.name) {
      metaPayload.oc_name = accessGroup.name;
    }
    if (accessGroup.description !== undefined && accessGroup.description !== current.description) {
      metaPayload.oc_beskrivelse = accessGroup.description || null;
    }
    if (Object.keys(metaPayload).length) {
      await dataverseApiCall(`/oc_tilgangsgruppes(${formatDataverseRecordKey(groupId)})`, {
        method: 'PATCH',
        body: JSON.stringify(metaPayload)
      });
    }

    await touchLastSynced(groupId);
    return loadMembers(groupId);
  };

  const importFromUnloc = async () => {
    const result = await listAccessGroups();
    if (!result) {
      throw new Error('Kunne ikke hente tilgangsgrupper fra Unloc.');
    }

    const unlocGroups = result.accessGroups || [];
    const existing = await getAllGroups({ includeInactive: true });
    const byUnlocId = new Map(
      existing.filter((group) => group.unlocId).map((group) => [group.unlocId, group])
    );

    const summary = {
      imported: 0,
      linked: 0,
      synced: 0
    };

    for (const unlocGroup of unlocGroups) {
      if (!unlocGroup?.id) {
        continue;
      }

      let group = byUnlocId.get(unlocGroup.id);
      if (!group) {
        const response = await dataverseApiCall('/oc_tilgangsgruppes', {
          method: 'POST',
          headers: { Prefer: 'return=representation' },
          body: JSON.stringify({
            oc_name: unlocGroup.name?.trim() || `Unloc ${unlocGroup.id.slice(0, 8)}`,
            oc_beskrivelse: unlocGroup.description?.trim() || null,
            oc_unlocid: unlocGroup.id
          })
        });
        group = mapGroup(response);
        byUnlocId.set(unlocGroup.id, group);
        summary.imported += 1;
      } else if (!group.unlocId) {
        await dataverseApiCall(`/oc_tilgangsgruppes(${formatDataverseRecordKey(group.id)})`, {
          method: 'PATCH',
          body: JSON.stringify({ oc_unlocid: unlocGroup.id })
        });
        summary.linked += 1;
      }

      await syncMembershipFromUnloc(group.id, unlocGroup);
      summary.synced += 1;
    }

    return summary;
  };

  /**
   * Enroll locks into default-for-locks groups and/or map-zone groups whose
   * polygon contains the lock coordinates. Associates only missing memberships.
   *
   * @param {Array<{ id: string, externalId?: string, latitude?: number|null, longitude?: number|null }>} locks
   */
  const reconcileLocksInMatchingGroups = async (locks = []) => {
    const summary = { locksProcessed: 0, associations: 0, errors: [] };
    const input = (locks || []).filter((lock) => lock?.id);
    if (!input.length) {
      return summary;
    }

    const [defaults, polygonGroups] = await Promise.all([
      listDefaultGroups('oc_defaultforlocks'),
      listPolygonGroups()
    ]);

    const byId = new Map();
    for (const group of defaults) {
      byId.set(group.id, group);
    }
    for (const group of polygonGroups) {
      byId.set(group.id, group);
    }

    const candidateGroups = [...byId.values()];
    if (!candidateGroups.length) {
      return summary;
    }

    const memberIdsByGroup = new Map();
    await Promise.all(
      candidateGroups.map(async (group) => {
        try {
          const members = await loadMembers(group.id);
          memberIdsByGroup.set(
            group.id,
            new Set(members.locks.map((lock) => String(lock.id).toLowerCase()))
          );
        } catch (error) {
          summary.errors.push(`${group.name}: ${error.message || error}`);
          memberIdsByGroup.set(group.id, new Set());
        }
      })
    );

    const polygonTargets = polygonGroups
      .map((group) => ({
        group,
        ring: geoJsonPolygonToRing(group.mapPolygon)
      }))
      .filter((entry) => entry.ring);

    for (const lock of input) {
      summary.locksProcessed += 1;
      const lockId = lock.id;
      const lockKey = String(lockId).toLowerCase();
      const externalId = lock.externalId || lockId;
      const latitude = toFiniteNumber(lock.latitude);
      const longitude = toFiniteNumber(lock.longitude);

      const targets = new Map();
      for (const group of defaults) {
        targets.set(group.id, group);
      }
      if (latitude != null && longitude != null) {
        for (const { group, ring } of polygonTargets) {
          if (pointInPolygon({ lat: latitude, lng: longitude }, ring)) {
            targets.set(group.id, group);
          }
        }
      }

      for (const group of targets.values()) {
        const members = memberIdsByGroup.get(group.id) || new Set();
        if (members.has(lockKey)) {
          continue;
        }

        try {
          await associateLock(group.id, lockId);
          members.add(lockKey);
          memberIdsByGroup.set(group.id, members);
          await enqueueMembershipSync({
            groupId: group.id,
            groupName: group.name,
            payload: { lockIdsToAdd: [externalId] }
          });
          summary.associations += 1;
        } catch (error) {
          if (isAlreadyAssociatedError(error)) {
            members.add(lockKey);
            memberIdsByGroup.set(group.id, members);
            continue;
          }
          summary.errors.push(
            `${lock.name || lockId} → ${group.name}: ${error.message || error}`
          );
        }
      }
    }

    return summary;
  };

  const enrollNewLockInMatchingGroups = async (
    lockId,
    { externalLockId, latitude, longitude, name } = {}
  ) => {
    const result = await reconcileLocksInMatchingGroups([
      {
        id: lockId,
        externalId: externalLockId || lockId,
        latitude,
        longitude,
        name
      }
    ]);
    return result;
  };

  const enrollNewLockInDefaultGroups = async (lockId, externalLockId) => {
    const groups = await listDefaultGroups('oc_defaultforlocks');
    if (!groups.length) {
      return [];
    }
    // No coordinates → reconcile only hits default-for-locks groups.
    await reconcileLocksInMatchingGroups([
      { id: lockId, externalId: externalLockId || lockId }
    ]);
    return groups;
  };

  const enrollNewPhoneInDefaultGroups = async (phoneId, mobileNumber) => {
    const groups = await listDefaultGroups('oc_defaultfortelefoner');
    if (!groups.length) {
      return [];
    }

    for (const group of groups) {
      await associatePhone(group.id, phoneId);
      await enqueueMembershipSync({
        groupId: group.id,
        groupName: group.name,
        payload: {
          appUserIdsToAdd: [mobileNumber],
          inviteNotification: SILENT_INVITE
        }
      });
    }

    return groups;
  };

  return {
    getAll: getAllGroups,

    getById: loadMembers,

    create: async ({
      name,
      description = '',
      defaultForLocks = false,
      defaultForPhones = false,
      mapPolygon = null
    }) => {
      const payload = {
        oc_name: name.trim(),
        oc_beskrivelse: description.trim() || null,
        oc_defaultforlocks: Boolean(defaultForLocks),
        oc_defaultfortelefoner: Boolean(defaultForPhones)
      };

      const polygonJson = serializeMapPolygon(mapPolygon);
      if (polygonJson) {
        payload.oc_mappolygon = polygonJson;
      }

      const response = await dataverseApiCall('/oc_tilgangsgruppes', {
        method: 'POST',
        headers: { Prefer: 'return=representation' },
        body: JSON.stringify(payload)
      });

      const group = mapGroup(response);
      await syncService.create({
        action: ACCESS_GROUP_SYNC_ACTION.CREATE_GROUP,
        groupId: group.id,
        groupName: group.name,
        payload: { name: group.name, description: group.description }
      });

      return group;
    },

    update: async (groupId, { name, description, defaultForLocks, defaultForPhones, mapPolygon }) => {
      const current = await loadMembers(groupId);
      const payload = {};

      if (name !== undefined) {
        payload.oc_name = name.trim();
      }
      if (description !== undefined) {
        payload.oc_beskrivelse = description.trim() || null;
      }
      if (defaultForLocks !== undefined) {
        payload.oc_defaultforlocks = Boolean(defaultForLocks);
      }
      if (defaultForPhones !== undefined) {
        payload.oc_defaultfortelefoner = Boolean(defaultForPhones);
      }
      if (mapPolygon !== undefined) {
        payload.oc_mappolygon = serializeMapPolygon(mapPolygon);
      }

      if (Object.keys(payload).length) {
        await dataverseApiCall(`/oc_tilgangsgruppes(${formatDataverseRecordKey(groupId)})`, {
          method: 'PATCH',
          body: JSON.stringify(payload)
        });
      }

      const metaChanged = name !== undefined || description !== undefined;
      if (metaChanged && current.unlocId) {
        await syncService.create({
          action: ACCESS_GROUP_SYNC_ACTION.UPDATE_GROUP_META,
          groupId,
          groupName: name ?? current.name,
          payload: {
            name: name ?? current.name,
            description: description ?? current.description
          }
        });
      }

      return loadMembers(groupId);
    },

    deactivate: async (groupId) => {
      const current = await loadMembers(groupId);
      await dataverseApiCall(`/oc_tilgangsgruppes(${formatDataverseRecordKey(groupId)})`, {
        method: 'PATCH',
        body: JSON.stringify({ statecode: 1, statuscode: 2 })
      });

      if (current.unlocId) {
        await syncService.create({
          action: ACCESS_GROUP_SYNC_ACTION.DELETE_GROUP,
          groupId,
          groupName: current.name
        });
      }
    },

    setMembership: async (groupId, { lockIds = [], phoneIds = [] }, { wait = false } = {}) => {
      const current = await loadMembers(groupId);
      const currentLockIds = new Set(current.locks.map((lock) => lock.id));
      const currentPhoneIds = new Set(current.phones.map((phone) => phone.id));
      const nextLockIds = new Set(lockIds);
      const nextPhoneIds = new Set(phoneIds);

      const locksToAdd = [...nextLockIds].filter((id) => !currentLockIds.has(id));
      const locksToRemove = [...currentLockIds].filter((id) => !nextLockIds.has(id));
      const phonesToAdd = [...nextPhoneIds].filter((id) => !currentPhoneIds.has(id));
      const phonesToRemove = [...currentPhoneIds].filter((id) => !nextPhoneIds.has(id));

      await applyMembershipLocal(groupId, { lockIds, phoneIds });

      const lockIdToExternal = new Map(current.locks.map((lock) => [lock.id, lock.externalId]));
      const phoneIdToMobile = new Map(current.phones.map((phone) => [phone.id, phone.mobileNumber]));

      if (locksToAdd.length) {
        const lockResponse = await dataverseApiCall(
          `/oc_locks?$select=oc_lockid,oc_externalid&$filter=${locksToAdd
            .map((id) => `oc_lockid eq ${formatDataverseRecordKey(id)}`)
            .join(' or ')}`
        );
        for (const row of lockResponse.value || []) {
          lockIdToExternal.set(row.oc_lockid, row.oc_externalid || row.oc_lockid);
        }
      }

      if (phonesToAdd.length) {
        const phoneResponse = await dataverseApiCall(
          `/oc_telefons?$select=oc_telefonid,oc_mobilnummer&$filter=${phonesToAdd
            .map((id) => `oc_telefonid eq ${formatDataverseRecordKey(id)}`)
            .join(' or ')}`
        );
        for (const row of phoneResponse.value || []) {
          phoneIdToMobile.set(row.oc_telefonid, row.oc_mobilnummer);
        }
      }

      const payload = {
        lockIdsToAdd: locksToAdd.map((id) => lockIdToExternal.get(id)).filter(Boolean),
        lockIdsToRemove: locksToRemove.map((id) => lockIdToExternal.get(id)).filter(Boolean),
        appUserIdsToAdd: phonesToAdd.map((id) => phoneIdToMobile.get(id)).filter(Boolean),
        appUserIdsToRemove: phonesToRemove.map((id) => phoneIdToMobile.get(id)).filter(Boolean)
      };

      const hasChanges =
        payload.lockIdsToAdd.length ||
        payload.lockIdsToRemove.length ||
        payload.appUserIdsToAdd.length ||
        payload.appUserIdsToRemove.length;

      if (hasChanges) {
        await enqueueMembershipSync({
          groupId,
          groupName: current.name,
          payload,
          wait
        });
      }

      return loadMembers(groupId);
    },

    syncMembershipFromUnloc,
    importFromUnloc,

    addLockToGroup: async (groupId, lockId, { wait = false } = {}) => {
      const current = await loadMembers(groupId);
      if (current.locks.some((lock) => lock.id === lockId)) {
        return current;
      }

      await associateLock(groupId, lockId);
      const lockResponse = await dataverseApiCall(
        `/oc_locks(${formatDataverseRecordKey(lockId)})?$select=oc_externalid`
      );
      const externalId = lockResponse.oc_externalid || lockId;

      await enqueueMembershipSync({
        groupId,
        groupName: current.name,
        payload: { lockIdsToAdd: [externalId] },
        wait
      });

      return loadMembers(groupId);
    },

    addPhoneToGroup: async (groupId, phoneId, { wait = false } = {}) => {
      const current = await loadMembers(groupId);
      if (current.phones.some((phone) => phone.id === phoneId)) {
        return current;
      }

      await associatePhone(groupId, phoneId);
      const phoneResponse = await dataverseApiCall(
        `/oc_telefons(${formatDataverseRecordKey(phoneId)})?$select=oc_mobilnummer`
      );

      await enqueueMembershipSync({
        groupId,
        groupName: current.name,
        payload: { appUserIdsToAdd: [phoneResponse.oc_mobilnummer] },
        wait
      });

      return loadMembers(groupId);
    },

    listGroupsForLock: async (lockId) => {
      const key = formatDataverseRecordKey(lockId);
      const response = await dataverseApiCall(
        `/oc_locks(${key})?$select=oc_lockid&$expand=${LOCK_NAV}($select=${GROUP_SELECT})`
      );
      return (response[LOCK_NAV] || []).map((row) => mapGroup(row));
    },

    listGroupsForPhone: async (phoneId) => {
      const key = formatDataverseRecordKey(phoneId);
      const response = await dataverseApiCall(
        `/oc_telefons(${key})?$select=oc_telefonid&$expand=${PHONE_NAV}($select=${GROUP_SELECT})`
      );
      return (response[PHONE_NAV] || []).map((row) => mapGroup(row));
    },

    enrollNewLockInMatchingGroups,
    reconcileLocksInMatchingGroups,
    enrollNewLockInDefaultGroups,
    enrollNewPhoneInDefaultGroups,
    enrollNewLockInDefaultGroup: async (lockId, externalLockId) => {
      const groups = await enrollNewLockInDefaultGroups(lockId, externalLockId);
      return groups[0] || null;
    },
    enrollNewPhoneInDefaultGroup: async (phoneId, mobileNumber) => {
      const groups = await enrollNewPhoneInDefaultGroups(phoneId, mobileNumber);
      return groups[0] || null;
    },

    /** Remove phone from every tilgangsgruppe + enqueue Unloc appUserIdsToRemove (MobilLogoff/Auto Logout). */
    removePhoneFromAllGroups: async (phoneId, mobileNumber) => {
      const mobile = mobileNumber || (await dataverseApiCall(
        `/oc_telefons(${formatDataverseRecordKey(phoneId)})?$select=oc_mobilnummer`
      )).oc_mobilnummer;
      const groups = await (async () => {
        const key = formatDataverseRecordKey(phoneId);
        const response = await dataverseApiCall(
          `/oc_telefons(${key})?$select=oc_telefonid&$expand=${PHONE_NAV}($select=${GROUP_SELECT})`
        );
        return (response[PHONE_NAV] || []).map((row) => mapGroup(row));
      })();

      for (const group of groups) {
        try {
          await disassociatePhone(group.id, phoneId);
        } catch {
          // Already removed
        }
        await enqueueMembershipSync({
          groupId: group.id,
          groupName: group.name,
          payload: {
            appUserIdsToRemove: [mobile],
            inviteNotification: SILENT_INVITE
          }
        });
      }

      return groups.length;
    },

    syncService
  };
}
